import { useEffect, useMemo, useState, type FormEvent } from 'react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  createAgentAuthUser,
} from '@/services/authService';
import {
  deleteAgent,
  saveAgent,
  setAgentActive,
  subscribeAgents,
} from '@/services/supportService';
import type { AgentRole, SupportAgent } from '@/types/support';
import { cn } from '@/lib/utils';

const PAGE_SIZE = 8;

const emptyForm = {
  name: '',
  email: '',
  password: '',
  role: 'agent' as AgentRole,
  phone: '',
  profileImage: '',
  maxConcurrentChats: 3,
  active: true,
  workingStart: '09:00',
  workingEnd: '18:00',
  timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC',
};

export function AdminSupportPage() {
  const [agents, setAgents] = useState<SupportAgent[]>([]);
  const [search, setSearch] = useState('');
  const [sort, setSort] = useState<'name' | 'email' | 'updatedAt'>('name');
  const [page, setPage] = useState(0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => subscribeAgents(setAgents), []);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    let list = agents;
    if (q) {
      list = list.filter(
        (a) =>
          a.name.toLowerCase().includes(q) ||
          a.email.toLowerCase().includes(q) ||
          a.role.toLowerCase().includes(q),
      );
    }
    list = [...list].sort((a, b) => {
      if (sort === 'updatedAt') return (b.updatedAt || 0) - (a.updatedAt || 0);
      return String(a[sort] || '').localeCompare(String(b[sort] || ''));
    });
    return list;
  }, [agents, search, sort]);

  const pageCount = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const pageItems = filtered.slice(page * PAGE_SIZE, page * PAGE_SIZE + PAGE_SIZE);

  const startEdit = (a: SupportAgent) => {
    setEditingId(a.agentId);
    setForm({
      name: a.name,
      email: a.email,
      password: '',
      role: a.role,
      phone: a.phone || '',
      profileImage: a.profileImage || '',
      maxConcurrentChats: a.maxConcurrentChats || 3,
      active: a.active,
      workingStart: a.workingHours?.start || '09:00',
      workingEnd: a.workingHours?.end || '18:00',
      timezone: a.workingHours?.timezone || emptyForm.timezone,
    });
    setError(null);
  };

  const resetForm = () => {
    setEditingId(null);
    setForm(emptyForm);
    setError(null);
  };

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError(null);
    try {
      let agentId = editingId;
      if (!agentId) {
        if (!form.password || form.password.length < 6) {
          throw new Error('Password must be at least 6 characters for new agents.');
        }
        agentId = await createAgentAuthUser(form.email, form.password, form.name);
      }
      await saveAgent(agentId, {
        name: form.name,
        email: form.email,
        role: form.role,
        active: form.active,
        phone: form.phone || null,
        profileImage: form.profileImage || null,
        maxConcurrentChats: form.maxConcurrentChats,
        workingHours: {
          start: form.workingStart,
          end: form.workingEnd,
          timezone: form.timezone,
          days: [1, 2, 3, 4, 5],
        },
      });
      resetForm();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="h-full overflow-y-auto">
    <div className="p-4 md:p-8 max-w-6xl mx-auto space-y-6 pb-24 md:pb-8">
      <div>
        <h1 className="text-xl font-semibold tracking-tight">Support members</h1>
        <p className="text-sm text-muted-foreground mt-1">Add, edit, enable/disable agents.</p>
      </div>

      <form onSubmit={(e) => void onSubmit(e)} className="rounded-xl border border-border p-4 grid gap-3 sm:grid-cols-2">
        <h2 className="sm:col-span-2 text-sm font-medium">{editingId ? 'Edit agent' : 'Add agent'}</h2>
        <div className="space-y-1.5">
          <Label>Name</Label>
          <Input value={form.name} onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))} required />
        </div>
        <div className="space-y-1.5">
          <Label>Email</Label>
          <Input type="email" value={form.email} onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))} required disabled={Boolean(editingId)} />
        </div>
        {!editingId && (
          <div className="space-y-1.5">
            <Label>Temp password</Label>
            <Input type="password" value={form.password} onChange={(e) => setForm((f) => ({ ...f, password: e.target.value }))} required />
          </div>
        )}
        <div className="space-y-1.5">
          <Label>Role</Label>
          <select
            className="flex h-9 w-full rounded-md border border-input bg-transparent px-3 text-sm"
            value={form.role}
            onChange={(e) => setForm((f) => ({ ...f, role: e.target.value as AgentRole }))}
          >
            <option value="agent">Agent</option>
            <option value="lead">Lead</option>
            <option value="admin">Admin</option>
          </select>
        </div>
        <div className="space-y-1.5">
          <Label>Phone</Label>
          <Input value={form.phone} onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label>Profile image URL</Label>
          <Input value={form.profileImage} onChange={(e) => setForm((f) => ({ ...f, profileImage: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label>Max concurrent chats</Label>
          <Input
            type="number"
            min={1}
            max={20}
            value={form.maxConcurrentChats}
            onChange={(e) => setForm((f) => ({ ...f, maxConcurrentChats: Number(e.target.value) || 1 }))}
          />
        </div>
        <div className="space-y-1.5">
          <Label>Working hours start</Label>
          <Input type="time" value={form.workingStart} onChange={(e) => setForm((f) => ({ ...f, workingStart: e.target.value }))} />
        </div>
        <div className="space-y-1.5">
          <Label>Working hours end</Label>
          <Input type="time" value={form.workingEnd} onChange={(e) => setForm((f) => ({ ...f, workingEnd: e.target.value }))} />
        </div>
        <label className="flex items-center gap-2 text-sm sm:col-span-2">
          <input type="checkbox" checked={form.active} onChange={(e) => setForm((f) => ({ ...f, active: e.target.checked }))} />
          Active
        </label>
        {error && <p className="text-sm text-destructive sm:col-span-2">{error}</p>}
        <div className="flex gap-2 sm:col-span-2">
          <Button type="submit" disabled={saving}>{saving ? 'Saving…' : editingId ? 'Update' : 'Create'}</Button>
          {editingId && (
            <Button type="button" variant="outline" onClick={resetForm}>Cancel</Button>
          )}
        </div>
      </form>

      <div className="flex flex-col sm:flex-row flex-wrap gap-2 items-stretch sm:items-center">
        <Input
          className="sm:max-w-xs"
          placeholder="Search name, email, role…"
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            setPage(0);
          }}
        />
        <select
          className="h-9 rounded-md border border-input bg-transparent px-3 text-sm"
          value={sort}
          onChange={(e) => setSort(e.target.value as typeof sort)}
        >
          <option value="name">Sort by name</option>
          <option value="email">Sort by email</option>
          <option value="updatedAt">Sort by updated</option>
        </select>
      </div>

      {/* Mobile agent cards */}
      <div className="md:hidden space-y-2">
        {pageItems.length === 0 && (
          <div className="rounded-xl border border-border p-8 text-center text-sm text-muted-foreground">No agents yet.</div>
        )}
        {pageItems.map((a) => (
          <div key={a.agentId} className="rounded-xl border border-border p-3 space-y-3">
            <div className="flex items-center gap-3">
              {a.profileImage ? (
                <img src={a.profileImage} alt="" className="h-10 w-10 rounded-full object-cover" />
              ) : (
                <div className="h-10 w-10 rounded-full bg-muted" />
              )}
              <div className="min-w-0 flex-1">
                <div className="font-medium truncate">{a.name}</div>
                <div className="text-xs text-muted-foreground truncate">{a.email}</div>
              </div>
              <span className={cn('text-[10px] px-2 py-0.5 rounded-full shrink-0', a.active ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-slate-500/15')}>
                {a.active ? 'Active' : 'Off'}
              </span>
            </div>
            <div className="text-xs text-muted-foreground capitalize">
              {a.role} · max {a.maxConcurrentChats} chats
            </div>
            <div className="grid grid-cols-3 gap-1.5">
              <Button size="sm" variant="outline" onClick={() => startEdit(a)}>Edit</Button>
              <Button size="sm" variant="secondary" onClick={() => void setAgentActive(a.agentId, !a.active)}>
                {a.active ? 'Disable' : 'Enable'}
              </Button>
              <Button size="sm" variant="destructive" onClick={() => void deleteAgent(a.agentId)}>Delete</Button>
            </div>
          </div>
        ))}
      </div>

      <div className="hidden md:block overflow-x-auto rounded-xl border border-border">
        <table className="w-full text-sm">
          <thead className="bg-muted/40 text-left">
            <tr>
              <th className="p-3 font-medium">Agent</th>
              <th className="p-3 font-medium">Role</th>
              <th className="p-3 font-medium">Status</th>
              <th className="p-3 font-medium">Max chats</th>
              <th className="p-3 font-medium">Actions</th>
            </tr>
          </thead>
          <tbody>
            {pageItems.length === 0 && (
              <tr>
                <td colSpan={5} className="p-6 text-center text-muted-foreground">No agents yet.</td>
              </tr>
            )}
            {pageItems.map((a) => (
              <tr key={a.agentId} className="border-t border-border">
                <td className="p-3">
                  <div className="flex items-center gap-2">
                    {a.profileImage ? (
                      <img src={a.profileImage} alt="" className="h-8 w-8 rounded-full object-cover" />
                    ) : (
                      <div className="h-8 w-8 rounded-full bg-muted" />
                    )}
                    <div>
                      <div className="font-medium">{a.name}</div>
                      <div className="text-xs text-muted-foreground">{a.email}</div>
                    </div>
                  </div>
                </td>
                <td className="p-3 capitalize">{a.role}</td>
                <td className="p-3">
                  <span className={cn('text-xs px-2 py-0.5 rounded-full', a.active ? 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300' : 'bg-slate-500/15 text-slate-600 dark:text-slate-300')}>
                    {a.active ? 'Active' : 'Disabled'}
                  </span>
                </td>
                <td className="p-3">{a.maxConcurrentChats}</td>
                <td className="p-3">
                  <div className="flex flex-wrap gap-1.5">
                    <Button size="sm" variant="outline" onClick={() => startEdit(a)}>Edit</Button>
                    <Button size="sm" variant="secondary" onClick={() => void setAgentActive(a.agentId, !a.active)}>
                      {a.active ? 'Disable' : 'Enable'}
                    </Button>
                    <Button size="sm" variant="destructive" onClick={() => void deleteAgent(a.agentId)}>Delete</Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-between text-sm gap-2">
        <span className="text-muted-foreground text-xs sm:text-sm">
          Page {page + 1} / {pageCount} · {filtered.length} agents
        </span>
        <div className="flex gap-2">
          <Button size="sm" variant="outline" disabled={page === 0} onClick={() => setPage((p) => p - 1)}>Prev</Button>
          <Button size="sm" variant="outline" disabled={page >= pageCount - 1} onClick={() => setPage((p) => p + 1)}>Next</Button>
        </div>
      </div>
    </div>
    </div>
  );
}
